import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import Colors from '../../constants/Colors';
import { FILTER_ALLOW_ALL } from '../../store/reducers/pantry';
import FilledButton from '../FilledButton';
import Svg from '../Svg';
import MultiSelect from '../form/MultiSelect';

import { AppText } from '../AppText';

const styles = StyleSheet.create({
  view: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: Colors.grayBorder,
  },
  topnav: {
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  topnavHeading: {
    fontSize: 18,
    fontWeight: '600',
  },
  topnavActionButton: {
    color: Colors.primaryColor,
    textAlign: 'right',
  },
  topnavElement: {
    padding: 20,
    width: '30%',
    textAlign: 'center',
  },
  section: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  sectionHeading: {
    flexWrap: 'wrap',
    fontFamily: 'roboto-regular',
    color: Colors.veryDarkGray,
    fontSize: 14,
    fontWeight: '600',
    paddingBottom: 10,
  },
  button: {
    margin: 20,
  },
});

/**
 * type ProgramType = 'non-food' | 'foodpantry' | 'soupkitchen'
 * type PantryStatus = 'opennow' | 'opentoday' | 'openthisweek' | 'other'
 * type PantryType = 'plentifulpantry' | 'verifiedpartnerpantry' | 'unverifiedpartnerpantry'
 * type ServiceType = 'line' | 'reservation' | 'pre-registration' | 'qr-code'
 * type Filter = {
 *  program_type: ProgramType[],
 *  status: PantryStatus[],
 *  pantry_type: PantryType[],
 *  service_type: ServiceType[],
 * }
 */

// must keep keys and values in sync with translation keys (Languages.js) and API filter params
const FILTER_OPTIONS /* : Array<{ type: T extends keyof Filter, values: Filter[T] }>*/ = [
  { type: 'program_type', values: ['non-food', 'foodpantry', 'soupkitchen'] },
  { type: 'status', values: ['opennow', 'opentoday', 'openthisweek', 'other'] },
  { type: 'pantry_type', values: ['plentifulpantry', 'verifiedpartnerpantry', 'unverifiedpartnerpantry'] },
  { type: 'service_type', values: ['line', 'reservation', 'pre-registration', 'qr-code'] },
];

/**
 * A full-page view that contains a list of filters for searching pantries.
 *
 * The filter state is stored in redux as state.pantry.filter
 * - all filters are always available, even if the displayed pantries don't have any values for that filter
 * - updating the filter state will not immediately trigger a new API call - getPantries() must be called elsewhere
 */
function PantryFilter(props) {
  const { actions, pantry, translation, navigation } = props;
  const { filter } = pantry;
  const [selectedFilter, setSelectedFilter] = useState(filter);

  const filterSections = FILTER_OPTIONS.map(({ type, values }) => {
    const translationKey = `pantry_filter_${type}`;
    const translatedFilterType = translation[translationKey];
    return {
      type,
      heading: translatedFilterType,
      options: values.map((value) => {
        return {
          value,
          selected: selectedFilter[type].includes(value),
          label: translation[`${translationKey}_${value}`],
        };
      }),
    };
  });

  const onSelectionChanged = (filterType) => (selectedValues) => {
    const newFilter = {
      ...selectedFilter,
      [filterType]: selectedValues,
    };
    setSelectedFilter(newFilter);
  };

  const resetFilter = () => {
    setSelectedFilter(FILTER_ALLOW_ALL);
  };

  const applyNewFilterAndClose = () => {
    actions.setPantryFilter(selectedFilter);
    navigation.setParams({ showFilter: undefined });
  };

  return (
    <View style={styles.view}>
      <View style={styles.topnav}>
        <TouchableOpacity style={styles.topnavElement} onPress={applyNewFilterAndClose}>
          <View style={styles.caretContainer}>
            <Svg icon="arrow_back" width={14} height={9} color={Colors.veryDarkGray} />
          </View>
        </TouchableOpacity>
        <AppText style={[styles.topnavElement, styles.topnavHeading]}>Filter</AppText>
        <TouchableOpacity style={styles.topnavElement} onPress={resetFilter}>
          <AppText style={styles.topnavActionButton}>Reset</AppText>
        </TouchableOpacity>
      </View>
      <ScrollView>
        <View style={styles.filterSectionsLayout}>
          {filterSections.map(({ type, heading, options }) => {
            return (
              <View key={type} style={styles.section}>
                <AppText style={styles.sectionHeading}>{heading}</AppText>
                <MultiSelect options={options} onSelectionChanged={onSelectionChanged(type)} />
              </View>
            );
          })}
        </View>
      </ScrollView>
      <View style={styles.button}>
        <FilledButton
          label="Apply Filters"
          onPress={applyNewFilterAndClose}
          color={Colors.primaryColor}
          full
        />
      </View>
    </View>
  );
}

PantryFilter.propTypes = {
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  pantry: PropTypes.objectOf(PropTypes.any).isRequired,
  navigation: PropTypes.objectOf(PropTypes.any),
};

export default PantryFilter;
